<script setup lang="ts">
import CompaniesDataTable from '@/features/companies/data-tables/CompaniesDataTable.vue'
import { storeToRefs } from 'pinia'
import {
  CompanyState,
  CompanyType,
  CreateCompanyFromAdminCreationType,
  ListCompaniesFromAdminCompanyDto as Company,
} from '@swipe4work/models-orval'
import { useDialog } from 'primevue'
import CreateCompanyFormDialog from '@/features/companies/dialogs/CreateCompanyFormDialog.vue'
import { InfoTableValue } from '@/shared/components/cards/content/InfoTable.vue'
import { useAuthStore } from '@/shared/stores/AuthStore'
import { useListAdminCompanies } from '@swipe4work/api-orval'
import { AdminPermissionResource, PermissionLevel } from '@swipe4work/models-orval'
import { usePermissions } from '@/shared/composables/UsePermissions'

definePage({
  path: '/sales/leads',
  name: 'companies.leads.overview',
  meta: {
    title: 'Lead Overzicht',
    breadcrumbParent: 'companies.overview',
    permission: {
      resource: 'Company',
      level: 'Read',
    },
  },
})

const dialog = useDialog()
const router = useRouter()
const authStore = useAuthStore()
const { user } = storeToRefs(authStore)

const { can } = usePermissions()
const canCompanyWrite = computed(() => can(AdminPermissionResource.Company, PermissionLevel.Write))

const { data: companiesData } = useListAdminCompanies(
  {
    Limit: 1000,
    Offset: 0, 
  },
  { query: { staleTime: 5 * 60 * 1000 } },
)
const companies = computed(() => companiesData.value?.companies ?? [])

function handleClickRow(event: Company) {
  router.push({
    name: 'companies.[id]',
    params: { id: event.id },
  })
}

const leadCompanies = computed(() => {
  return companies.value.filter((company) => company.type === CompanyType.Lead)
})

const route = useRoute()

// Ideal-only filter is persisted in the URL (`ideal=1`) so a filtered view can be shared.
const idealOnly = computed<boolean>({
  get: () => route.query.ideal === '1',
  set: (value) => {
    const rest = { ...route.query }

    delete rest.ideal

    router.replace({
      query: value
        ? {
          ...rest,
          ideal: '1',
        }
        : rest,
    })
  },
})

const visibleLeadCompanies = computed(() =>
  idealOnly.value ? leadCompanies.value.filter((company) => company.isIdeal) : leadCompanies.value,
)

const managedCompanies = computed(() => {
  return leadCompanies.value.filter((company) => company.accountManager?.externalId === user.value?.localAccountId)
})

const unhandledManagedLeads = computed(() => {
  return managedCompanies.value.filter((company) => company.type === CompanyType.Lead && !company.statistics.accountManagerFollowedUpAt)
})

function createCompany() {
  dialog.open(CreateCompanyFormDialog, {
    props: {
      modal: true,
      closable: true,
      showHeader: false,
      dismissableMask: true,
    },
    data: {
      creationType: CreateCompanyFromAdminCreationType.Lead,
    },
  })
}

const leadStatusInformation = computed((): InfoTableValue[] => {
  return [
    {
      label: 'Aantal lead bedrijven',
      value: leadCompanies.value.length.toString(),
    },
    {
      label: 'Onbehandelde leads',
      value: leadCompanies.value.filter((company) => !company.statistics.accountManagerFollowedUpAt).length.toString(),
    },
    {
      isDivider: true,
    },
    {
      label: 'Wachten op kandidaat',
      value: leadCompanies.value.filter((company) => company.status.state === CompanyState.WaitingForCandidate).length.toString(),
    },
    {
      label: 'Mail versturen mislukt',
      value: leadCompanies.value.filter((company) => company.status.state === CompanyState.FailedEmailDelivery).length.toString(),
    },
    {
      label: 'Kandidaat mail ontvangen',
      value: leadCompanies.value.filter((company) => company.status.state === CompanyState.CandidateMailReceived).length.toString(),
    },
    {
      label: 'Ingelogd op portal',
      value: leadCompanies.value.filter((company) => company.status.state === CompanyState.LoggedInToPortal).length.toString(),
    },
    {
      label: 'Kandidaat beoordeeld',
      value: leadCompanies.value.filter((company) => company.status.state === CompanyState.CandidateEvaluated).length.toString(),
    },
  ]
})

const managedLeadStatusInformation = computed((): InfoTableValue[] => {
  return [
    {
      label: 'Aantal lead bedrijven',
      value: managedCompanies.value.length.toString(),
    },
    {
      label: 'Onbehandelde leads',
      value: managedCompanies.value.filter((company) => !company.statistics.accountManagerFollowedUpAt).length.toString(),
    },
    {
      isDivider: true,
    },
    {
      label: 'Wachten op kandidaat',
      value: managedCompanies.value.filter((company) => company.status.state === CompanyState.WaitingForCandidate).length.toString(),
    },
    {
      label: 'Mail versturen mislukt',
      value: managedCompanies.value.filter((company) => company.status.state === CompanyState.FailedEmailDelivery).length.toString(),
    },
    {
      label: 'Kandidaat mail ontvangen',
      value: managedCompanies.value.filter((company) => company.status.state === CompanyState.CandidateMailReceived).length.toString(),
    },
    {
      label: 'Ingelogd op portal',
      value: managedCompanies.value.filter((company) => company.status.state === CompanyState.LoggedInToPortal).length.toString(),
    },
    {
      label: 'Kandidaat beoordeeld',
      value: managedCompanies.value.filter((company) => company.status.state === CompanyState.CandidateEvaluated).length.toString(),
    },
  ]
})
</script>

<template>
  <TwoColumnLayout
    :col-span-one="3"
    :col-span-two="9"
  >
    <template #one>
      <CardWithHeader
        class="max-lg:!hidden"
        title="Lead Informatie"
      >
        <InfoTable :data="leadStatusInformation" />
      </CardWithHeader>

      <CardWithHeader
        class="max-lg:!hidden"
        title="Jouw lead informatie"
      >
        <InfoTable :data="managedLeadStatusInformation" />
      </CardWithHeader>
    </template>

    <template #two>
      <CompaniesDataTable
        view-key="companies.leads.unhandled"
        :companies="unhandledManagedLeads"
        :card-options="{
          title: 'Onbehandelde leads in beheer',
        }"
        :rows="5"
        @row-click="(e) => router.push({
          name: 'companies.[id]',
          params: { id: e.id },
        })"
        :sort="{
          field: 'lastActionAt',
          order: 1,
        }"
        :show-columns="{
          name: true,
          companyType: false,
          subscription: false,
          createdAt: false,
          actions: true,
          jobCategories: true,
          accountManager: false,
          status: true,
          statistics: false,
          lastActionAt: true,
        }"
      />

      <label class="flex cursor-pointer items-center gap-2 text-sm text-surface-700">
        <ToggleSwitch v-model="idealOnly" input-id="ideal-only" />
        Alleen Ideal Companies
      </label>

      <CompaniesDataTable
        view-key="companies.leads.overview"
        :card-options="{
          title: 'Leads',
          action: canCompanyWrite ? {
            label: 'Nieuwe lead',
            icon: 'fa-regular fa-plus',
            onClick: createCompany,
          } : undefined,
        }"
        :companies="visibleLeadCompanies"
        @row-click="handleClickRow"
        :sort="{
          field: 'status.lastActionAt',
          order: -1,
        }"
        :show-columns="{
          name: true,
          companyType: false,
          subscription: false,
          createdAt: false,
          jobCategories: true,
          actions: true,
          accountManager: true,
          status: true,
          statistics: true,
          lastActionAt: true,
        }"
      >
        <template #statistics="{ company }">
          <Checkbox :model-value="company.statistics.accountManagerFollowedUpAt !== undefined" binary disabled />
        </template>
      </CompaniesDataTable>
    </template>
  </TwoColumnLayout>
</template>
